import { Option } from 'types/Generics';
import { IntakeFormPreferences } from 'types/IntakeForm';
import { OnlineBookingPreferences } from 'types/OnlineBooking';
import { PetSize, PetTypeId, PetTypeIds } from 'types/Pet';

export interface Marketplace {
    id: number;
    slug: string;
    name?: string;
    logo?: string;
    phone?: string;
    timeZone: string;

    businessType: MarketplaceType;

    intakeFormPreferences: IntakeFormPreferences;
    onlineBookingPreferences: OnlineBookingPreferences;

    address?: MarketplaceAddress;

    deposit?: MarketplaceDeposit;

    specialHours: Array<SpecialHour>;

    vaccinesByType: Record<PetTypeId, Array<Option<string>>>;

    catsEnabled?: boolean;
    dogsEnabled?: boolean;
    exoticsEnabled?: boolean;

    petTypes: MarketplacePetTypes;

    scheduleType: MarketplaceScheduleType;
    schedule: MarketplaceSchedule;

    settings: MarketplaceSettings;

    evaluationEnabled: boolean;

    gaTrackingId?: string;
    gtmTrackingId?: string;
}

export interface MarketplaceSettings {
    boardingEnabled: boolean;
    daycareEnabled: boolean;
    groomingEnabled: boolean;
    mobileGroomingEnabled: boolean;
    trainingEnabled: boolean;
    walkingEnabled: boolean;

    morningFeedingEnabled: boolean;
    morningFeedingTime: { hour: number; minute: number } | null;
    afternoonFeedingEnabled: boolean;
    afternoonFeedingTime: { hour: number; minute: number } | null;
    eveningFeedingEnabled: boolean;
    eveningFeedingTime: { hour: number; minute: number } | null;

    leadsEnabled: boolean;

    customDisclaimerEnabled: boolean;
    customDisclaimerMessage: string;

    requireVaccinesEnabled: boolean;
    cardOnFileEnabled: boolean;
    intakeFormCardOnFileEnabled: boolean;

    waitlistEnabled: boolean;

    brandPrimaryColor: string | null;
    brandSecondaryColor: string | null;
}

export enum MarketplaceServiceCategory {
    DENTAL = 'dental',
    GROOMING = 'grooming',
    DAYCARE = 'daycare',
    BOARDING = 'boarding',
    EVALUATION = 'evaluation',
    TRAINING = 'training',
    WALKING = 'walking',
    TRANSPORTATION = 'transportation'
}

export interface MarketplaceDeposit {
    value: number;
    category: MarketplaceDepositCategory;

    applicability: MarketplaceDepositApplicability;
    newClientsOnly: boolean;
    rules: Array<MarketplaceDepositRule>;
}

export enum MarketplaceDepositCategory {
    NONE = 'none',
    FIXED = 'amount',
    PERCENTAGE = 'percentage'
}

export enum MarketplaceDepositApplicability {
    ALL = 'all',
    SPECIFIC = 'specific'
}

/**
 * A per-category deposit rule. `categoryId` is kept as a raw string rather than
 * a `MarketplaceServiceCategory` because the backend owns the category catalog
 * and may add values this app does not know about yet — an unknown id simply
 * never matches a booking item instead of breaking the conversion.
 */
export interface MarketplaceDepositRule {
    categoryId: string;
    category: MarketplaceDepositCategory;
    value: number;
}

export interface SpecialHour {
    id: number;
    name: string;
    date: string;
    closed: boolean;
    businessHours: {
        open: string;
        close: string;
    };
}

export enum MarketplaceType {
    INHOME = 'inhome',
    MOBILE = 'mobile',
    ATHOME = 'athome',
    LOCAL_BUSINESS = 'b&m'
}

export interface MarketplaceAddress {
    id: number;
    addressLineOne: string;
    addressLineTwo: string;
    city: string;
    state: string;
    zipcode: string;
    country: string;
    latitude: number;
    longitude: number;
}

export enum MarketplaceScheduleType {
    Work = 'work_schedule',
    MultiSlots = 'multi_slots'
}

export const emptyMarketplace: Marketplace = {
    id: 0,
    slug: '',
    timeZone: 'UTC',

    businessType: MarketplaceType.LOCAL_BUSINESS,

    logo: '',
    name: '',
    phone: '',

    address: undefined,

    specialHours: [],

    vaccinesByType: {
        [PetTypeIds.DOG]: [],
        [PetTypeIds.CAT]: [],
        [PetTypeIds.EXOTIC]: []
    },

    onlineBookingPreferences: {
        enabled: false,
        bookingDateLimits: {
            from: null,
            to: null
        },
        allowNewClients: false,
        allowSelectStaff: false,
        bookingRequestCategories: {},
        promoImageUrl: '',
        pricingOption: 'starting_at'
    },

    intakeFormPreferences: {
        enabled: false,
        questionsEnabled: false,
        questions: []
    },

    petTypes: {
        [PetTypeIds.DOG]: {
            enabled: false,
            sizes: [],
            hairTypes: [],
            hairLengths: []
        },
        [PetTypeIds.CAT]: {
            enabled: false,
            sizes: [],
            hairTypes: [],
            hairLengths: []
        },
        [PetTypeIds.EXOTIC]: {
            enabled: false,
            sizes: [],
            hairTypes: [],
            hairLengths: []
        }
    },

    scheduleType: MarketplaceScheduleType.Work,
    schedule: {
        monday: null,
        tuesday: null,
        wednesday: null,
        thursday: null,
        friday: null,
        saturday: null,
        sunday: null
    },

    settings: {
        boardingEnabled: false,
        daycareEnabled: false,
        groomingEnabled: false,
        mobileGroomingEnabled: false,
        trainingEnabled: false,
        walkingEnabled: false,

        morningFeedingEnabled: false,
        morningFeedingTime: null,
        afternoonFeedingEnabled: false,
        afternoonFeedingTime: null,
        eveningFeedingEnabled: false,
        eveningFeedingTime: null,

        leadsEnabled: false,

        customDisclaimerEnabled: false,
        customDisclaimerMessage: '',

        requireVaccinesEnabled: true,
        cardOnFileEnabled: false,
        intakeFormCardOnFileEnabled: false,

        waitlistEnabled: false,

        brandPrimaryColor: null,
        brandSecondaryColor: null
    },

    evaluationEnabled: false,

    gaTrackingId: undefined,
    gtmTrackingId: undefined
};

export interface MarketplaceTypeDogs {
    ages: Array<Option<string>>;
    sizes: Array<MarketplacePetSize>;
    hairTypes: Array<Option<string>>;
    hairLengths: Array<Option<string>>;
}

export interface MarketplaceTypeCats {
    ages: Array<Option<string>>;
    sizes?: Array<MarketplacePetSize>;
    hairTypes: Array<Option<string>>;
    hairLengths: Array<Option<string>>;
}

export interface MarketplaceTypeExotics {
    types: Array<Option<string>>;
    others: Array<string>;
    sizes?: Array<MarketplacePetSize>;
}

export interface MarketplacePetSize {
    id: string;
    name: string;
    description?: string;
    minWeight: number | null;
    maxWeight: number | null;
}

export interface MarketplaceSchedule {
    sunday: MarketplaceScheduleRange;
    monday: MarketplaceScheduleRange;
    tuesday: MarketplaceScheduleRange;
    wednesday: MarketplaceScheduleRange;
    thursday: MarketplaceScheduleRange;
    friday: MarketplaceScheduleRange;
    saturday: MarketplaceScheduleRange;
}

export type MarketplaceScheduleRange = {
    open: string;
    close: string;
} | null;

export type MarketplacePetTypes = Record<PetTypeId, MarketplacePetType>;

export interface MarketplacePetType {
    enabled: boolean;
    sizes: Array<PetSize>;
    hairTypes: Array<Option<string>>;
    hairLengths: Array<Option<string>>;
    types?: Array<MarketplaceExoticType>;
    others?: Array<string>;
}

export interface MarketplaceExoticType {
    id: string;
    name: string;
    description?: string;
}
